import {DI_TOKENS} from '@/di/di.tokens';
import {AnalyticsTaskButtonsType} from '@/services/TrackingService/TrackingService.constants';
import {TaskCta} from '@/stores/TasksStore/TasksStore.types';

import {injected} from 'brandi';

import {HttpError} from '@specials/project-user';

import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {TasksStore} from '@/stores/TasksStore/TasksStore';

export class TasksService {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly tasksStore: TasksStore,
    private readonly loggerService: LoggerService,
    private readonly platformService: PlatformService,
    private readonly trackingService: TrackingService,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
  ) {}

  public async fetchTasks(): Promise<void> {
    try {
      this.tasksStore.setLoadingFetchStatus();
      this.tasksStore.clearErrorFetchData();

      const response = await this.projectApi.fetchTasks();

      this.tasksStore.setTasks({tasks: response.payload.tasks, isNeedTaskSort: true});
      this.tasksStore.setNextAvailableTaskDate(response.payload.nextAvailableTaskDate);

      this.tasksStore.setSuccessFetchStatus();
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.tasksStore.setErrorFetchStatus();
        this.tasksStore.setErrorFetchData(error);
      }
    }
  }

  public instantReward = async (taskId: string): Promise<void> => {
    const taskTitle = this.tasksStore.getTaskTitleById(taskId);
    const subtaskIds = this.tasksStore.getSubtaskIdsForClaimReward(taskId);

    try {
      if (!this.tasksStore.isEnabledMocks()) {
        await this.projectApi.instantReward(taskId, subtaskIds);
        this.trackingService.trackClickToTaskButtons(taskTitle, AnalyticsTaskButtonsType.GetReward);
      }
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }

      throw error;
    }
  };

  public updateTasksStatusAfterClaimReward(taskId: string): void {
    const subtaskIds = this.tasksStore.getSubtaskIdsForClaimReward(taskId);
    this.tasksStore.updateTasksStatusAfterClaimReward(taskId, subtaskIds);

    const reward = this.tasksStore.getTotalRewardAfterClaimRewards(taskId, subtaskIds);

    if (reward.multiTap > 0) {
      this.clickerStateStore.addMultiplier(reward.multiTap);
    }
    this.clickerStateStore.addEnergyChargeCount(reward.energyRecharge);
  }

  public seenTasks = async (): Promise<void> => {
    const seenTasksIds = this.tasksStore.getSeenTasksIds();

    if (seenTasksIds.length) {
      try {
        if (!this.tasksStore.isEnabledMocks()) {
          await this.projectApi.seenTasks(seenTasksIds);
        }
        this.tasksStore.clearSeenTasksIds();
      } catch (error) {
        this.loggerService.logHttpError(error as HttpError);

        if (this.errorsService.isErrorRecalculationLeagues(error)) {
          this.gameStatusService.setStatusLeaguesRecalculation();
        }
      }
    }
  };

  public clickTaskLink = async (taskId: string): Promise<void> => {
    const subtaskId = this.tasksStore.getInProgressSubtaskId(taskId);

    if (!subtaskId) {
      this.loggerService.logHaveNotSubtaskForClickLinkError(taskId);
      return;
    }

    try {
      if (!this.tasksStore.isEnabledMocks()) {
        await this.projectApi.clickTaskLink(taskId, subtaskId);
      }
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }
    }
  };

  public clearTasksStatus(): void {
    this.tasksStore.setIdleFetchStatus();
  }

  public clearTasksErrorData(): void {
    this.tasksStore.clearErrorFetchData();
  }

  public onNewTaskViewIntersection(taskId: string): void {
    this.tasksStore.onNewTaskViewIntersection(taskId);
  }

  public getCtaLink = (cta: TaskCta): string => {
    if (this.platformService.isInvesting() && cta.investAppDeeplink) {
      return cta.investAppDeeplink;
    }

    if (this.platformService.isMb() && cta.deeplink) {
      return cta.deeplink;
    }

    if (this.platformService.detectIsIos() && cta.iosLink) {
      return cta.iosLink;
    }

    return cta.link;
  };

  public deleteIdFromDoneTasksIdsForStartShowAnimation = (taskId: string): void => {
    this.tasksStore.deleteIdFromDoneTasksIdsForStartShowAnimation(taskId);
  };

  public deleteIdFromProgressTasksIdsForStartShowAnimation = (taskId: string): void => {
    this.tasksStore.deleteIdFromProgressTasksIdsForStartShowAnimation(taskId);
  };

  public enableTasksMocks = (): void => {
    this.tasksStore.setEnabledMocks(true);
  };

  public trackClickToTaskLink = (taskId: string): void => {
    const taskTitle = this.tasksStore.getTaskTitleById(taskId);
    this.trackingService.trackClickToTaskButtons(taskTitle, AnalyticsTaskButtonsType.GoTo);
  };

  public trackClickToCopyLink = (taskId: string): void => {
    const taskTitle = this.tasksStore.getTaskTitleById(taskId);
    this.trackingService.trackClickToTaskButtons(taskTitle, AnalyticsTaskButtonsType.CopyLink);
  };

  public trackClickToShareButton = (taskId: string): void => {
    const taskTitle = this.tasksStore.getTaskTitleById(taskId);
    this.trackingService.trackClickToTaskButtons(taskTitle, AnalyticsTaskButtonsType.FriendInvitation);
  };
}

injected(
  TasksService,
  DI_TOKENS.projectApi,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.tasksStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.platformService,
  DI_TOKENS.trackingService,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
);
